import { Icon } from "@platform/ui/components";
import { focusRingStyles } from "@platform/ui/styles";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import * as React from "react";
import ReactDOM from "react-dom";
import { mergeRefs } from "react-merge-refs";
import { twMerge } from "tailwind-merge";
import { VariantProps, tv } from "tailwind-variants";
import { PopoverContainerProvider } from "~/shared/components/ds/Popover";
import { useElementStore } from "~/shared/stores/element-store";

export function Sheet({ children, ...rest }: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Root>) {
  return (
    <SheetPrimitive.Root {...rest}>
      <SheetFooterActionsPortalProvider>{children}</SheetFooterActionsPortalProvider>
    </SheetPrimitive.Root>
  );
}

export const SheetTrigger = SheetPrimitive.Trigger;

export const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...rest }, ref) => (
  <SheetPrimitive.Overlay
    className={twMerge(
      "bg-ds-scrim data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50",
      className,
    )}
    {...rest}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

interface LayoutSheetProps
  extends
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof layoutSheetContentVariants> {}
export const LayoutSheet = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, LayoutSheetProps>(
  ({ size = "sm", className, children, ...rest }, ref) => {
    const [contentRef, setContentRef] = React.useState<HTMLDivElement | null>(null);
    const layoutSheetRef = useElementStore((state) => state.elements.layoutSheetRef);

    return (
      <PopoverContainerProvider container={contentRef}>
        <SheetPortal container={layoutSheetRef ?? undefined}>
          <SheetOverlay aria-label="Close" className="bg-ds-scrim">
            <SheetPrimitive.Content
              ref={mergeRefs([ref, setContentRef])}
              className={layoutSheetContentVariants({ size, className })}
              {...rest}
            >
              {children}
              <SheetPrimitive.Close
                className={focusRingStyles({
                  className:
                    "ring-offset-ds-surface-foundation data-[state=open]:bg-ds-surface-foundation absolute top-6 right-6 z-30 h-4 w-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none",
                })}
              >
                <Icon className="size-4" symbol="close-line" />
                <span className="sr-only">Close</span>
              </SheetPrimitive.Close>
            </SheetPrimitive.Content>
          </SheetOverlay>
        </SheetPortal>
      </PopoverContainerProvider>
    );
  },
);
LayoutSheet.displayName = "LayoutSheet";

const layoutSheetContentVariants = tv({
  base: "fixed gap-4 flex outline-hidden flex-col overflow-y-auto bg-ds-surface-foundation shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 w-full top-2 rounded-tl-3xl rounded-bl-3xl right-0 h-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
  variants: {
    size: {
      sm: "sm:max-w-sm",
      md: "sm:max-w-[530px]",
      lg: "sm:max-w-[730px]",
    },
  },
  defaultVariants: {
    size: "sm",
    side: "right",
  },
});

export const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={twMerge(
      "border-ds-stroke-primary bg-ds-surface-foundation sticky inset-0 z-20 flex flex-col gap-2 border-b p-6 outline-hidden",
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

export const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...rest }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={twMerge("text-ds-text-primary m-0 text-lg font-medium", className)}
    {...rest}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

export const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...rest }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={twMerge("text-ds-text-secondary m-0 text-sm", className)}
    {...rest}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export const SheetContent = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...rest }, ref) => {
    return (
      <div
        className={twMerge("relative z-10 flex max-h-full flex-1 flex-col overflow-y-auto", className)}
        ref={ref}
        {...rest}
      />
    );
  },
);
SheetContent.displayName = "SheetContent";

export const SheetFooter = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...rest }, ref) => {
    const [, setFooterActionsPortal] = React.useContext(SheetFooterActionsPortalContext) ?? [];

    return (
      <div
        className={twMerge(
          "bg-ds-surface-foundation border-ds-stroke-primary sticky bottom-0 border-t p-6 empty:hidden",
          className,
        )}
        ref={mergeRefs([ref, setFooterActionsPortal])}
        {...rest}
      />
    );
  },
);
SheetFooter.displayName = "SheetFooter";

const SheetFooterActionsPortalContext = React.createContext<
  [HTMLDivElement | null, React.Dispatch<React.SetStateAction<HTMLDivElement | null>>] | null
>(null);

interface SheetFooterActionsPortalProviderProps {
  children: React.ReactNode;
}
export function SheetFooterActionsPortalProvider({ children }: SheetFooterActionsPortalProviderProps) {
  const sheetFooterActionsPortalState = React.useState<HTMLDivElement | null>(null);

  return (
    <SheetFooterActionsPortalContext.Provider value={sheetFooterActionsPortalState}>
      {children}
    </SheetFooterActionsPortalContext.Provider>
  );
}

export const SheetFooterActions = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...rest }, ref) => {
    return <div ref={ref} className={twMerge("flex justify-end gap-4", className)} {...rest} />;
  },
);

export function SheetFooterActionsPortal({ children }: { children: React.ReactNode }) {
  const [portalContentElement] = React.useContext(SheetFooterActionsPortalContext) ?? [];

  return portalContentElement ? ReactDOM.createPortal(children, portalContentElement) : null;
}
